import { useMemo } from 'react';
import qrcode from 'qrcode-generator';

const QUIET = 4;

/** Module matrix, row runs. */
export function qrRuns(value: string): { count: number; runs: Array<[row: number, col: number, len: number]> } {
  const qr = qrcode(0, 'M');
  qr.addData(value);
  qr.make();
  const count = qr.getModuleCount();
  const runs: Array<[number, number, number]> = [];
  for (let r = 0; r < count; r++) {
    let c = 0;
    while (c < count) {
      if (!qr.isDark(r, c)) {
        c++;
        continue;
      }
      const start = c;
      while (c < count && qr.isDark(r, c)) c++;
      runs.push([r, start, c - start]);
    }
  }
  return { count, runs };
}

export function QrCode({ value, label }: { value: string; label: string }) {
  const { count, runs } = useMemo(() => qrRuns(value), [value]);
  const box = count + QUIET * 2;
  const d = runs.map(([r, c, len]) => `M${c} ${r}h${len}v1h-${len}z`).join('');
  return (
    <svg className="co-qr-svg" viewBox={`${-QUIET} ${-QUIET} ${box} ${box}`} role="img" aria-label={label} shapeRendering="crispEdges">
      <rect x={-QUIET} y={-QUIET} width={box} height={box} fill="#fff" />
      <path d={d} fill="#0b1020" />
    </svg>
  );
}

/** Branded PNG for gallery upload. */
export async function downloadQr(value: string, opts: { amount: string; reference: string; filename: string }): Promise<void> {
  const { count, runs } = qrRuns(value);
  const cell = Math.max(6, Math.floor(600 / (count + QUIET * 2)));
  const qrSize = (count + QUIET * 2) * cell;
  const pad = 48;
  const width = qrSize + pad * 2;
  const height = qrSize + 260;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.textAlign = 'center';
  ctx.fillStyle = '#0b1020';
  ctx.font = '700 34px Inter, system-ui, sans-serif';
  ctx.fillText('Partner AI', width / 2, 70);
  ctx.fillStyle = '#5b6478';
  ctx.font = '400 20px Inter, system-ui, sans-serif';
  ctx.fillText('Isi saldo lewat QRIS', width / 2, 104);

  const top = 130;
  ctx.fillStyle = '#0b1020';
  for (const [r, c, len] of runs) ctx.fillRect(pad + (c + QUIET) * cell, top + (r + QUIET) * cell, len * cell, cell);

  ctx.fillStyle = '#0b1020';
  ctx.font = '700 36px Inter, system-ui, sans-serif';
  ctx.fillText(opts.amount, width / 2, top + qrSize + 56);
  ctx.fillStyle = '#5b6478';
  ctx.font = '400 18px ui-monospace, Menlo, monospace';
  ctx.fillText(opts.reference, width / 2, top + qrSize + 92);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = opts.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2_000);
}
